import type Phaser from 'phaser';
import { HubTicketSchema, InboxSchema, openInvites, type Invite } from '../meta/online/hub';
import type { FriendRequestRow } from '../meta/online/schema';
import { HubSocket, type HubTicketAnswer } from '../platform/hub';
import { onAppHidden, onAppShown } from '../platform/native';
import { apiRequest, ONLINE_ENABLED, probablyOnline } from '../platform/online';
import { linkExit } from './linkExit';
import { openRaceLink } from './links';
import { whoText } from './nicknames';
import { markFriend, onlineOn } from './online';
import { saves } from './records';
import type { HudScene } from './scenes/HudScene';
import type { InviteData } from './scenes/InviteScene';

/**
 * The player's hub, game side (docs/FRIENDS.md, step 2): keeps the hub's socket open while the app
 * is open with online play on (which shows the player online to friends, unless they hid it),
 * holds the invites waiting, and shows a new one: a card over a menu or the results, a small note
 * on the HUD mid-lawn, nothing during a live race or match (the card waits for its results).
 */

let socket: HubSocket | null = null;
let invites: Invite[] = [];
/** Friend requests waiting for this player's answer (docs/FRIENDS.md, step 3). */
let requests: FriendRequestRow[] = [];
/** An invite that arrived and has not been shown as a card yet. */
let unseen: Invite | null = null;
/** A tapped friend alert's invite (by room code), joined once the inbox has it. */
let wanted: string | null = null;
/** That invite, found: off to its lobby as soon as the screen allows. */
let joining: Invite | null = null;
/** Invites already noted on the HUD (by room code), so each is noted once. */
const noted = new Set<string>();
const listeners = new Set<() => void>();
let game: Phaser.Game | null = null;

/** The invites waiting, newest first. */
export function hubInvites(): readonly Invite[] {
  return openInvites(invites, Date.now());
}

/** Friend requests waiting for an answer, newest first. */
export function hubRequests(): readonly FriendRequestRow[] {
  return requests;
}

/** Whether anything waits in the inbox (the envelope's and the CLUB tab's dot). */
export function inboxNews(): boolean {
  return hubInvites().length > 0 || requests.length > 0;
}

/** Asks for the whole inbox again (invites and friend requests). */
export async function refreshInbox(): Promise<void> {
  const token = saves().save.online.player?.token;
  if (!token || !onlineOn()) return;
  const answer = await apiRequest('/inbox', { token });
  if (answer.status !== 200) return;
  const parsed = InboxSchema.safeParse(answer.body);
  if (!parsed.success) return;
  invites = parsed.data.invites;
  requests = parsed.data.requests;
  changed();
}

/** Calls `fn` whenever the invites change; returns the way to stop. */
export function onHub(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function changed(): void {
  if (wanted) {
    const invite = invites.find((i) => i.code === wanted);
    if (invite) {
      wanted = null;
      joining = invite;
      present();
    }
  }
  for (const fn of listeners) fn();
}

/**
 * A friend alert for an invite was tapped: straight to its lobby, as soon as the inbox has it
 * (owner, 2026-10-31: tapping the alert is the answer; no card asking again).
 */
export function showInviteFromPush(code: string): void {
  wanted = code;
  changed();
  void refreshInbox();
}

/** A tapped alert's screen, opened once the game is past loading (a tap can launch the app). */
let opening: { scene: string; data?: object; at: number } | null = null;
/** How long a tapped alert's screen waits for the game to be ready before it is let go. */
const OPEN_WAIT_MS = 20_000;

/** A friend alert for a friend request was tapped: the INBOX, unless mid-lawn. */
export function openInboxFromPush(): void {
  opening = { scene: 'Inbox', at: Date.now() };
  openTapped();
}

/** A tapped "someone beat your Daily time" alert: today's Daily board. */
export function openDailyBoardFromPush(): void {
  opening = { scene: 'Boards', data: { open: 'daily' }, at: Date.now() };
  openTapped();
}

/**
 * Opens the tapped alert's screen when it can: not while loading or on the title (asked again each
 * second), and not over a lawn in play, a lobby, a card or a reveal (then it is let go: the player
 * is busy).
 */
function openTapped(): void {
  const g = game;
  const want = opening;
  if (!g || !want) return;
  if (Date.now() - want.at > OPEN_WAIT_MS) {
    opening = null;
    return;
  }
  const active = g.scene.getScenes(true).map((s) => s.scene.key);
  // Still loading, or on the title (its wipe to the menu would replace the screen opened now).
  if (active.length === 0 || active.includes('Boot') || active.includes('Title')) return;
  opening = null;
  if (active.some((k) => BUSY.includes(k))) return;
  const exit = linkExit(active);
  if (!exit || exit === 'wait') return;
  g.scene.getScene(exit.from)?.scene.start(want.scene, want.data);
}

/** Starts the hub for the game's life: connects now if it can, and as the app comes and goes. */
export function startHub(g: Phaser.Game): void {
  if (!ONLINE_ENABLED) return;
  game = g;
  connect();
  onAppShown(connect);
  onAppHidden(disconnect);
  // Online turned on, or the game joined, since: connect then. Online turned off: let go.
  setInterval(() => {
    if (!able()) disconnect();
    else connect();
    present();
    openTapped();
  }, 1000);
}

function able(): boolean {
  return onlineOn() && !!saves().save.online.player && !document.hidden;
}

function connect(): void {
  if (socket || !able()) return;
  socket = new HubSocket({
    ticket,
    onOpen: () => {
      socket?.send({ type: 'hello', visible: saves().save.settings.showOnline });
      void refreshInbox();
    },
    onMessage: (m) => {
      if (m.type === 'inbox') {
        invites = m.invites;
        changed();
      } else if (m.type === 'changed') {
        void refreshInbox();
      } else if (m.type === 'invite') {
        invites = openInvites(
          [m.invite, ...invites.filter((i) => i.code !== m.invite.code)],
          Date.now(),
        );
        unseen = m.invite;
        changed();
        present();
      }
    },
  });
}

function disconnect(): void {
  socket?.close();
  socket = null;
}

async function ticket(): Promise<HubTicketAnswer> {
  const token = saves().save.online.player?.token;
  if (!token) return { stop: true };
  if (!probablyOnline()) return { retry: true };
  const answer = await apiRequest('/me/hub/ticket', { method: 'POST', token });
  if (answer.status === 'network' || answer.status >= 500 || answer.status === 429) {
    return { retry: true };
  }
  const parsed = HubTicketSchema.safeParse(answer.body);
  if (answer.status === 200 && parsed.success) return parsed.data;
  return { stop: true };
}

/** Shows or hides this player's online dot to friends (Settings > ONLINE). */
export function setShowOnline(visible: boolean): void {
  saves().update((s) => (s.settings.showOnline = visible));
  socket?.send({ type: 'hello', visible });
}

/** Answers a friend request: accepted (friends both ways) or declined (quietly). True when done. */
export async function answerFriendRequest(sender: string, accept: boolean): Promise<boolean> {
  const token = saves().save.online.player?.token;
  if (!token) return false;
  const answer = await apiRequest(`/friends/requests/${sender}/${accept ? 'accept' : 'decline'}`, {
    method: 'POST',
    token,
  });
  if (answer.status !== 204 && answer.status !== 404) return false;
  if (accept && answer.status === 204) markFriend(sender);
  requests = requests.filter((r) => r.player !== sender);
  changed();
  return true;
}

/** Lets an invite go, here and on the server. */
export function dismissInvite(code: string): void {
  invites = invites.filter((i) => i.code !== code);
  if (unseen?.code === code) unseen = null;
  changed();
  const token = saves().save.online.player?.token;
  if (token) void apiRequest(`/inbox/invites/${code}`, { method: 'DELETE', token });
}

/** Joins an invite's room: off to the lobby (the menu opens it), the invite let go. */
export function joinInvite(scene: Phaser.Scene, invite: Invite): void {
  dismissInvite(invite.code);
  openRaceLink(scene.game, invite.code);
}

/** Invites a friend to the player's room; null when sent, or what went wrong. */
export async function inviteToRoom(code: string, friend: string): Promise<string | null> {
  const token = saves().save.online.player?.token;
  if (!token) return 'Not online.';
  const answer = await apiRequest(`/rooms/${code}/invite`, {
    method: 'POST',
    token,
    body: { friend },
  });
  if (answer.status === 204) return null;
  if (answer.status === 409) return 'The room is full.';
  if (answer.status === 429) return 'Slow down a little.';
  return "Couldn't send it.";
}

/** Live play: nothing may cover it (its results may). */
const LIVE = ['RaceLevel', 'DuelLevel'];
/** Screens a card must not cover: a lobby already, a card already, a reveal. */
const BUSY = ['Race', 'Invite', 'Reveal'];

/** Shows the waiting invite if the screen allows: a card, or for now a note on the HUD. */
function present(): void {
  const g = game;
  if (g && joining) joinTapped(g, joining);
  const invite = unseen;
  if (!g || !invite) return;
  if (invite.expires <= Date.now() || !invites.some((i) => i.code === invite.code)) {
    unseen = null;
    return;
  }
  const active = g.scene.getScenes(true).map((s) => s.scene.key);
  if (active.some((k) => BUSY.includes(k))) return;
  const exit = linkExit(active);
  if (exit === 'wait') {
    // Mid-lawn: a quiet note now (not in live play), the card once the results are up.
    if (
      active.includes('Level') &&
      !active.some((k) => LIVE.includes(k)) &&
      !noted.has(invite.code)
    ) {
      noted.add(invite.code);
      const hud = g.scene.getScene('Hud') as HudScene | null;
      hud?.showNotice(`${whoText(invite.from.player, invite.from.name)} invited you!`);
    }
    return;
  }
  if (!exit) return;
  unseen = null;
  g.scene.getScene(exit.from)?.scene.launch('Invite', { invite } satisfies InviteData);
  g.scene.getScene('Invite')?.scene.bringToTop();
}

/**
 * The tapped alert's invite: joined (the lobby, through the menu) once the game is past loading.
 * Mid-lawn, or with a lobby, a card or a reveal already up, it waits as a card like any new
 * invite instead. An invite that has ended is let go.
 */
function joinTapped(g: Phaser.Game, invite: Invite): void {
  if (invite.expires <= Date.now() || !invites.some((i) => i.code === invite.code)) {
    joining = null;
    return;
  }
  const active = g.scene.getScenes(true).map((s) => s.scene.key);
  // Still loading (the art is not ready for the menu yet): asked again in a second.
  if (active.length === 0 || active.includes('Boot')) return;
  joining = null;
  const exit = linkExit(active);
  if (exit && exit !== 'wait' && !active.some((k) => BUSY.includes(k))) {
    dismissInvite(invite.code);
    openRaceLink(g, invite.code);
    return;
  }
  unseen = invite;
}
